import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "./api";
import { runRechargeRecovery } from "./lib/rechargeRecovery";

const AuthCtx = createContext(null);

const DEFAULT_CONFIG = {
  story_price: 3,
  pencil_price: 3,
  blueprint_price: 0,
  hint_price: 4,
  solution_price: 5,
  summary_price: 2,
  mcq_price: 2,
  free_wallet_credit: 100,
  currency_symbol: "₹",
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("dt_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [lang, setLang] = useState(() => localStorage.getItem("dt_lang") || "en");
  // Always start from in-memory defaults and fetch fresh `/api/config` on mount.
  // Reason: caching prices in localStorage means a price change on the server
  // is invisible until the user clears storage. The fetch below overrides the
  // defaults within ~100 ms, which is much better than showing stale prices.
  const [config, setConfig] = useState(DEFAULT_CONFIG);

  const persistUser = (u) => {
    setUser(u);
    if (u) localStorage.setItem("dt_user", JSON.stringify(u));
    else localStorage.removeItem("dt_user");
  };

  const login = (token, u) => {
    localStorage.setItem("dt_token", token);
    persistUser(u);
  };

  const logout = () => {
    localStorage.removeItem("dt_token");
    persistUser(null);
  };

  const refreshUser = useCallback(async () => {
    try {
      const r = await api.get("/me");
      persistUser(r.data);
      return r.data;
    } catch (_) {
      return null;
    }
  }, []);

  const updateBalance = (balance) => {
    if (user) {
      const next = { ...user, wallet_balance: balance };
      persistUser(next);
    }
  };

  const updateHelpedCount = (count) => {
    if (user) {
      const next = { ...user, helped_count: count };
      persistUser(next);
    }
  };

  // Silently pull the latest wallet balance from the server. Used by the
  // polling + visibility-refresh effect below so incoming P2P transfers
  // (Share Balance) reflect on the recipient's device without logout/login.
  const syncBalance = useCallback(async () => {
    try {
      const r = await api.get("/wallet");
      const bal = r?.data?.balance;
      if (typeof bal !== "number") return;
      setUser((prev) => {
        if (!prev) return prev;
        if (prev.wallet_balance === bal) return prev;  // no change → no re-render
        const next = { ...prev, wallet_balance: bal };
        try { localStorage.setItem("dt_user", JSON.stringify(next)); } catch (_) { /* ignore */ }
        return next;
      });
    } catch (_) { /* silent */ }
  }, []);

  const changeLang = (l) => {
    setLang(l);
    localStorage.setItem("dt_lang", l);
  };

  useEffect(() => {
    if (localStorage.getItem("dt_token") && !user) refreshUser();
  }, [refreshUser, user]);

  // App-wide recharge recovery: runs once whenever an authenticated user is
  // present. This rescues any payment that succeeded but was never credited
  // because the app/WebView was killed mid-flow. Runs after user is loaded so
  // the Authorization header is set.
  useEffect(() => {
    if (!user) return;
    let alive = true;

    const recover = (source) => {
      runRechargeRecovery({
        onCredited: ({ amount, balance, order_id }) => {
          if (!alive) return;
          try {
            const next = { ...user, wallet_balance: balance };
            setUser(next);
            try { localStorage.setItem("dt_user", JSON.stringify(next)); } catch (_) { /* ignore */ }
            toast.success(`Recovered payment — ₹${amount} added to your wallet!`);
            console.info("[recharge] recovery credited", { source, amount, balance, order_id });
            // If a pending payment was just credited and the user is NOT on the
            // wallet page (e.g. the app was killed mid-flow and reopened on
            // Landing), navigate them to /wallet so they see the updated
            // balance. We do this with a soft URL change to avoid coupling
            // AuthContext to react-router.
            if (
              typeof window !== "undefined" &&
              window.location &&
              window.location.pathname !== "/wallet"
            ) {
              try { window.history.pushState({}, "", "/wallet"); window.dispatchEvent(new PopStateEvent("popstate")); } catch (_e) { /* ignore */ }
            }
          } catch (e) {
            console.error("[recharge] onCredited handler threw", e);
          }
        },
      }).catch((e) => console.error("[recharge] recovery failed", { source }, e));
    };

    // Slight delay to avoid racing with page-level recovery on Wallet.
    const id = setTimeout(() => recover("mount"), 1500);

    const onVisible = () => {
      if (document.visibilityState === "visible") recover("visibilitychange");
    };
    const onFocus = () => recover("focus");
    const onPageShow = (e) => { if (e?.persisted) recover("pageshow_bfcache"); };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      alive = false;
      clearTimeout(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Global safety net — make sure no unhandled promise rejection or stray
  // window error from third-party SDKs (Razorpay checkout, MSG91) can crash
  // the React tree. Logs to console + best-effort toast.
  useEffect(() => {
    const onError = (e) => {
      console.error("[global] window error", e?.error || e?.message);
    };
    const onRejection = (e) => {
      console.error("[global] unhandled promise rejection", e?.reason);
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  // Poll the server for wallet-balance changes so incoming P2P Share Balance
  // transfers reflect on the recipient's device without a logout/login. Also
  // refreshes on visibility/focus/pageshow so returning to the app updates
  // immediately.
  useEffect(() => {
    if (!user?.id) return undefined;
    let alive = true;
    const safeSync = () => { if (alive) syncBalance(); };

    // Do one immediate sync on mount so a freshly-opened app is up-to-date.
    safeSync();

    // Poll every 20s while the tab is visible.
    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") safeSync();
    }, 20000);

    const onVisible = () => {
      if (document.visibilityState === "visible") safeSync();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", safeSync);
    window.addEventListener("pageshow", safeSync);

    return () => {
      alive = false;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", safeSync);
      window.removeEventListener("pageshow", safeSync);
    };
  }, [user?.id, syncBalance]);

  // Fetch dynamic backend config (prices, limits) once on mount — no localStorage cache.
  useEffect(() => {
    api.get("/config")
      .then((r) => setConfig(r.data))
      .catch(() => { /* keep defaults */ });
    // Also clear any legacy cached config so old installs don't show stale prices.
    try { localStorage.removeItem("dt_config"); } catch (_) { /* no-op */ }
  }, []);

  return (
    <AuthCtx.Provider value={{ user, lang, config, login, logout, refreshUser, updateBalance, updateHelpedCount, changeLang }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
